import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import SearchBar from './components/SearchBar';
import DrugResults from './components/DrugResults';
import { searchDrug } from './api/fda';
import DrugComparison from './components/DrugComparison';
import MyMeds from './components/MyMeds';

function getDrugFromURL() {
  return new URLSearchParams(window.location.search).get('drug') || '';
}

function setDrugInURL(drugName) {
  const url = drugName
    ? `${window.location.pathname}?drug=${encodeURIComponent(drugName)}`
    : window.location.pathname;
  if (window.location.pathname + window.location.search !== url) {
    window.history.pushState({}, '', url);
  }
}

function App() {
  const [drugData, setDrugData] = useState(null);
  const [comparisonDrug, setComparisonDrug] = useState(null);
  const [loading, setLoading] = useState(false);
  const [compareLoading, setCompareLoading] = useState(false);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [compareInput, setCompareInput] = useState('');
  const [pendingSection, setPendingSection] = useState(null);
  const currentDrugRef = useRef('');

  const examples = ['ozempic', 'isotretinoin', 'metformin', 'finasteride', 'adderall'];

  // When results land: jump to a requested section (e.g. recalls from a My Meds
  // card), otherwise start at the top of the new results. Also keep the tab title useful.
  useEffect(() => {
    if (!drugData) {
      document.title = 'Evidently — Search any drug. See the real evidence.';
      return;
    }
    document.title = `${drugData.drugInfo?.brandName || drugData.name} — Evidently`;
    if (pendingSection) {
      const target = pendingSection;
      setPendingSection(null);
      // Wait one frame so the section exists before scrolling
      requestAnimationFrame(() => {
        setTimeout(() => {
          document.getElementById(target)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      });
    } else {
      window.scrollTo({ top: 0 });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drugData]);

  // On first load and on back/forward, follow the ?drug= param
  useEffect(() => {
    const fromURL = getDrugFromURL();
    if (fromURL) setQuery(fromURL);

    const onPopState = () => {
      const drug = getDrugFromURL();
      // Hash-only changes (section nav pills) also fire popstate — don't re-search
      if (drug && drug.toLowerCase() === currentDrugRef.current.toLowerCase()) return;
      if (drug) {
        setQuery(drug);
      } else {
        setDrugData(null);
        setComparisonDrug(null);
        setCompareInput('');
        setError(null);
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const handleHome = () => {
    currentDrugRef.current = '';
    setDrugData(null);
    setComparisonDrug(null);
    setCompareInput('');
    setError(null);
    setQuery('');
    setDrugInURL('');
  };

  const handleNewSearch = (term) => {
    setQuery(term);
    setDrugData(null);
    setComparisonDrug(null);
    setCompareInput('');
  };

  const handleResults = (d) => {
    setDrugData(d);
    setComparisonDrug(null);
    setCompareInput('');
    if (d?.name) {
      currentDrugRef.current = d.name;
      setDrugInURL(d.name);
    }
  };

  const runCompare = async () => {
    if (!compareInput.trim() || compareLoading) return;
    setCompareLoading(true);
    const result = await searchDrug(compareInput.trim());
    setComparisonDrug(result);
    setCompareLoading(false);
    requestAnimationFrame(() => {
      setTimeout(() => {
        document.getElementById('comparison')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    });
  };

  const inResults = !!(drugData || loading);

  return (
    <div className="app">
      <div className="utility-bar">KNOW WHAT YOU'RE TAKING. SEE THE REAL EVIDENCE.</div>
      {inResults ? (
        <header className="app-header app-header--slim">
          <h1 className="app-logo app-logo--slim" onClick={handleHome}>
            <img src="/logo.svg" alt="" className="logo-mark logo-mark--slim" />
            Evidently
          </h1>
          <SearchBar
            setDrugData={handleResults}
            setLoading={setLoading}
            setError={setError}
            externalQuery={query}
            setExternalQuery={setQuery}
          />
        </header>
      ) : (
        <header className="app-header">
          <h1 className="app-logo" onClick={handleHome}>
            <img src="/logo.svg" alt="" className="logo-mark" />
            Evidently
          </h1>
          <p className="tagline">Search any drug. See the real evidence.</p>
        </header>
      )}

      <main>
        {!inResults && (
          <>
            <SearchBar
              setDrugData={handleResults}
              setLoading={setLoading}
              setError={setError}
              externalQuery={query}
              setExternalQuery={setQuery}
            />
            <div className="examples">
              <p className="examples-label">Popular searches</p>
              <div className="examples-list">
                {examples.map((drug) => (
                  <button key={drug} className="example-pill" onClick={() => setQuery(drug)}>
                    {drug}
                  </button>
                ))}
              </div>
            </div>

          <div className="evidence-explainer">
            <p className="explainer-label">Evidence Tier Range</p>
            <div className="tier-scale-pill">
              <span className="tier-scale-endpoint">Best</span>
              <span className="tier-scale-badge" style={{ backgroundColor: '#2e7d32' }}>✓</span>
              <span className="tier-scale-badge" style={{ backgroundColor: '#f57c00' }}>!</span>
              <span className="tier-scale-badge" style={{ backgroundColor: '#c62828' }}>!</span>
              <span className="tier-scale-badge" style={{ backgroundColor: '#8d8489' }}>?</span>
              <span className="tier-scale-endpoint">Unknown</span>
            </div>
            <div className="tier-scale-captions">
              <div className="tier-scale-caption">
                <span className="tier-scale-caption-name">Well-Studied</span>
                <span className="tier-scale-caption-desc">3+ Phase 3/4 trials · 2,000+ participants · long-term data exists</span>
              </div>
              <div className="tier-scale-caption">
                <span className="tier-scale-caption-name">Moderate</span>
                <span className="tier-scale-caption-desc">1+ Phase 3/4 trial · or 5+ trials total · some gaps remain</span>
              </div>
              <div className="tier-scale-caption">
                <span className="tier-scale-caption-name">Thin Evidence</span>
                <span className="tier-scale-caption-desc">Early-phase or small trials only · long-term safety unclear</span>
              </div>
              <div className="tier-scale-caption">
                <span className="tier-scale-caption-name">No FDA Data</span>
                <span className="tier-scale-caption-desc">No approval or trial data · may be supplement or unapproved compound</span>
              </div>
            </div>
            <p className="explainer-note">
              ⚠️ Evidence Tier is not a safety score. A well-studied drug can still carry serious risks. No FDA data does not mean safe — it means unknown.
            </p>
          </div>

          <p className="disclaimer">Surfaces publicly available FDA data. Not medical advice.</p>
          </>
        )}

        {loading && (
          <div className="loading-container">
            <div className="spinner" />
            <p className="status">Searching FDA databases...</p>
          </div>
        )}

        {error && (
          <div className="error-box">
            <p className="error">{error}</p>
            <p className="error-hint">Double-check the spelling, or try one of these:</p>
            <div className="examples-list" style={{ justifyContent: 'center' }}>
              {examples.map((drug) => (
                <button key={drug} className="example-pill" onClick={() => { setError(null); setQuery(drug); }}>
                  {drug}
                </button>
              ))}
            </div>
          </div>
        )}

        {drugData && (
          <>
            <DrugResults data={drugData} onSearch={handleNewSearch} />

            <div id="compare" className="compare-box">
              <div className="compare-header">
                <p className="compare-label">Compare with another drug</p>
                {comparisonDrug && (
                  <button className="compare-clear" onClick={() => { setComparisonDrug(null); setCompareInput(''); }}>
                    Clear ✕
                  </button>
                )}
              </div>
              <div className="compare-input-row">
                <input
                  className="compare-input"
                  placeholder="e.g. spironolactone, doxycycline..."
                  value={compareInput}
                  onChange={(e) => setCompareInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') runCompare(); }}
                />
                <button
                  className="compare-button"
                  disabled={compareLoading}
                  onClick={runCompare}
                >
                  {compareLoading ? <span className="compare-spinner" /> : 'Compare'}
                </button>
              </div>
            </div>

            {comparisonDrug && (
              <DrugComparison drug1={drugData} drug2={comparisonDrug} onSearch={handleNewSearch} />
            )}
          </>
        )}

        {!inResults && (
          <MyMeds onSelect={(name, section) => {
            if (section) setPendingSection(section);
            setQuery(name);
          }} />
        )}
      </main>

      <footer className="about-section">
        <h3>About Evidently</h3>
        <p>
          Finding clear, honest information about how well-studied a drug actually is shouldn't require a medical degree.
          The FDA and ClinicalTrials.gov publish all of this data publicly — Evidently puts it in one place.
        </p>
        <p className="about-note">
          Data sourced from openFDA and ClinicalTrials.gov. Updated in real time. Not medical advice.
        </p>
      </footer>
    </div>
  );
}

export default App;
